import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Shield, 
  Database, 
  FileText,
  Settings,
  Activity,
  CheckCircle2,
  ArrowRight,
  Play,
  Pause,
  Layers,
  ShieldCheck,
  Target,
  Award,
  Sparkles,
  Lock,
  Replace,
  Loader2,
  Zap
} from 'lucide-react';

interface OPMObject {
  id: string;
  name: string;
  type: 'object' | 'process' | 'state';
  x: number;
  y: number;
  width: number;
  height: number;
  description: string;
  states?: string[];
  currentState?: string;
  processes?: string[];
  connections?: string[];
}

interface OPMProcess {
  id: string;
  name: string;
  type: 'process';
  x: number;
  y: number;
  width: number;
  height: number;
  description: string;
  inputs: string[];
  outputs: string[];
  agents?: string[];
  instruments?: string[];
}

interface OPMAgent {
  id: string;
  name: string;
  type: 'agent';
  x: number;
  y: number;
  width: number;
  height: number;
  description: string;
}

interface AbstractionLevel {
  level: number;
  name: string;
  description: string;
  objects: OPMObject[];
  processes: OPMProcess[];
  agents: OPMAgent[];
  connections: any[];
  focus: string;
}

interface MBSEOPMFlowPhaseProps {
  onComplete?: (results: any) => void;
  isCompleted?: boolean;
}

const MBSEOPMFlow: React.FC<MBSEOPMFlowPhaseProps> = ({onComplete, isCompleted}) => {
  const [selectedLevel, setSelectedLevel] = useState<number>(0);
  const [isAnimating, setIsAnimating] = useState(false);
  const [animationStep, setAnimationStep] = useState(0);
  const [selectedObject, setSelectedObject] = useState<string | null>(null);
  const [showOPL, setShowOPL] = useState(false);
  const [expandedSections, setExpandedSections] = useState<string[]>(['overview']);
  const [companyData, setCompanyData] = useState<{
    company?: string;
    industry?: string;
    objective?: string;
    challenges?: string;
  }>({});

  useEffect(() => {
    // Read company data from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const data = {
      company: urlParams.get('company') || 'Skyflow',
      industry: urlParams.get('industry') || 'Data Privacy & AI Privacy',
      objective: urlParams.get('objective') || 'Pioneering Data Privacy Vaults to securely handle sensitive customer data with a simple API.',
      challenges: urlParams.get('challenges') || 'Intense competition, complex enterprise integration, evolving regulations, and AI privacy complexities.'
    };
    setCompanyData(data);
  }, []);

  // Define abstraction levels following OPM methodology
  const abstractionLevels: AbstractionLevel[] = [
    // L0 - System Level (Highest abstraction)
    {
      level: 0,
      name: "System Level",
      description: "Highest level view showing the complete data privacy ecosystem",
      focus: "Overall system purpose and external interactions",
      objects: [
        {
          id: 'privacy-ecosystem',
          name: 'Data Privacy Ecosystem',
          type: 'object',
          x: 450,
          y: 180,
          width: 280,
          height: 100,
          description: 'Complete end-to-end data privacy and protection system',
          states: ['Inactive', 'Processing', 'Protected'],
          currentState: 'Processing'
        }
      ],
      processes: [
        {
          id: 'protect-data',
          name: 'Protect Sensitive Data',
          type: 'process',
          x: 500,
          y: 320,
          width: 180,
          height: 50,
          description: 'High-level process to secure and govern sensitive data',
          inputs: ['privacy-ecosystem'],
          outputs: ['privacy-ecosystem']
        }
      ],
      agents: [
        {
          id: 'data-stakeholders',
          name: 'Data Stakeholders',
          type: 'agent',
          x: 120,
          y: 180,
          width: 180,
          height: 70,
          description: 'All parties involved in data handling: sources, users, regulators'
        },
        {
          id: 'compliance-framework',
          name: 'Regulatory Framework',
          type: 'agent',
          x: 880,
          y: 180,
          width: 180,
          height: 70,
          description: 'GDPR, HIPAA, PCI-DSS and other regulatory requirements'
        }
      ],
      connections: []
    },
    
    // L1 - Subsystem Level
    {
      level: 1,
      name: "Subsystem Level",
      description: "Major functional subsystems and their interactions",
      focus: "Primary data flow and protection mechanisms",
      objects: [
        {
          id: 'sensitive-data',
          name: 'Sensitive Data',
          type: 'object',
          x: 180,
          y: 200,
          width: 120,
          height: 60,
          description: 'PII, PHI, PCI data requiring protection',
          states: ['Raw', 'Classified', 'Isolated'],
          currentState: 'Raw'
        },
        {
          id: 'vault-system',
          name: 'Privacy Vault System',
          type: 'object',
          x: 450,
          y: 200,
          width: 150,
          height: 60,
          description: 'Central secure storage and processing system',
          states: ['Empty', 'Secured', 'Governed'],
          currentState: 'Empty'
        },
        {
          id: 'privacy-tokens',
          name: 'Privacy Tokens',
          type: 'object',
          x: 720,
          y: 200,
          width: 120,
          height: 60,
          description: 'Non-sensitive format-preserving tokens',
          states: ['Generated', 'Active', 'Expired'],
          currentState: 'Generated'
        },
        {
          id: 'governance-policies',
          name: 'Governance Policies',
          type: 'object',
          x: 450,
          y: 380,
          width: 150,
          height: 60,
          description: 'Access control and compliance rules',
          states: ['Defined', 'Active', 'Updated'],
          currentState: 'Active'
        }
      ],
      processes: [
        {
          id: 'data-ingestion',
          name: 'Ingest & Classify',
          type: 'process',
          x: 315,
          y: 120,
          width: 140,
          height: 45,
          description: 'Discover, classify and prepare sensitive data',
          inputs: ['sensitive-data'],
          outputs: ['vault-system']
        },
        {
          id: 'tokenization',
          name: 'Tokenize & Replace',
          type: 'process',
          x: 585,
          y: 120,
          width: 140,
          height: 45,
          description: 'Generate privacy-preserving tokens',
          inputs: ['vault-system'],
          outputs: ['privacy-tokens']
        },
        {
          id: 'access-control',
          name: 'Govern Access',
          type: 'process',
          x: 585,
          y: 300,
          width: 140,
          height: 45,
          description: 'Control and audit data access',
          inputs: ['privacy-tokens'],
          outputs: [],
          instruments: ['governance-policies']
        }
      ],
      agents: [
        {
          id: 'data-sources',
          name: 'Data Sources',
          type: 'agent',
          x: 80,
          y: 200,
          width: 80,
          height: 60,
          description: 'External systems providing data'
        },
        {
          id: 'data-consumers',
          name: 'Data Consumers',
          type: 'agent',
          x: 960,
          y: 200,
          width: 100,
          height: 60,
          description: 'Applications and users consuming data'
        }
      ],
      connections: []
    },

    // L2 - Component Level  
    {
      level: 2,
      name: "Component Level",
      description: "Detailed components and their specific functions",
      focus: "Individual components and their detailed interactions",
      objects: [
        {
          id: 'pii-data',
          name: 'PII Data',
          type: 'object',
          x: 120,
          y: 120,
          width: 90,
          height: 45,
          description: 'Personally Identifiable Information',
          states: ['Discovered', 'Classified'],
          currentState: 'Discovered'
        },
        {
          id: 'phi-data',
          name: 'PHI Data',
          type: 'object',
          x: 120,
          y: 180,
          width: 90,
          height: 45,
          description: 'Protected Health Information',
          states: ['Discovered', 'Classified'],
          currentState: 'Discovered'
        },
        {
          id: 'pci-data',
          name: 'PCI Data',
          type: 'object',
          x: 120,
          y: 240,
          width: 90,
          height: 45,
          description: 'Payment Card Industry Data',
          states: ['Discovered', 'Classified'],
          currentState: 'Discovered'
        },
        {
          id: 'encryption-engine',
          name: 'Encryption Engine',
          type: 'object',
          x: 350,
          y: 140,
          width: 130,
          height: 50,
          description: 'Polymorphic encryption system',
          states: ['Idle', 'Encrypting', 'Complete'],
          currentState: 'Idle'
        },
        {
          id: 'token-vault',
          name: 'Token Vault',
          type: 'object',
          x: 350,
          y: 220,
          width: 130,
          height: 50,
          description: 'Secure token storage and management',
          states: ['Empty', 'Storing', 'Managing'],
          currentState: 'Empty'
        },
        {
          id: 'access-logs',
          name: 'Access Logs',
          type: 'object',
          x: 350,
          y: 300,
          width: 130,
          height: 50,
          description: 'Audit trail and access records',
          states: ['Logging', 'Stored', 'Archived'],
          currentState: 'Logging'
        },
        {
          id: 'format-tokens',
          name: 'Format-Preserving Tokens',
          type: 'object',
          x: 620,
          y: 140,
          width: 150,
          height: 50,
          description: 'Tokens maintaining original data format',
          states: ['Generated', 'Valid', 'Revoked'],
          currentState: 'Generated'
        },
        {
          id: 'api-keys',
          name: 'API Keys',
          type: 'object',
          x: 620,
          y: 220,
          width: 150,
          height: 50,
          description: 'Authentication and authorization tokens',
          states: ['Active', 'Expired', 'Revoked'],
          currentState: 'Active'
        },
        {
          id: 'policy-rules',
          name: 'Policy Rules',
          type: 'object',
          x: 860,
          y: 200,
          width: 120,
          height: 50,
          description: 'Granular access control rules',
          states: ['Defined', 'Enforced', 'Updated'],
          currentState: 'Enforced'
        }
      ],
      processes: [
        {
          id: 'data-discovery',
          name: 'Discover Data',
          type: 'process',
          x: 250,
          y: 80,
          width: 110,
          height: 35,
          description: 'Scan and identify sensitive data',
          inputs: ['pii-data', 'phi-data', 'pci-data'],
          outputs: ['encryption-engine']
        },
        {
          id: 'classification',
          name: 'Classify Data',
          type: 'process',
          x: 250,
          y: 380,
          width: 110,
          height: 35,
          description: 'Categorize data by sensitivity',
          inputs: ['pii-data', 'phi-data', 'pci-data'],
          outputs: ['encryption-engine']
        },
        {
          id: 'encrypt-data',
          name: 'Encrypt Data',
          type: 'process',
          x: 520,
          y: 80,
          width: 110,
          height: 35,
          description: 'Apply polymorphic encryption',
          inputs: ['encryption-engine'],
          outputs: ['token-vault']
        },
        {
          id: 'generate-tokens',
          name: 'Generate Tokens',
          type: 'process',
          x: 520,
          y: 380,
          width: 110,
          height: 35,
          description: 'Create format-preserving tokens',
          inputs: ['token-vault'],
          outputs: ['format-tokens', 'api-keys']
        },
        {
          id: 'validate-access',
          name: 'Validate Access',
          type: 'process',
          x: 800,
          y: 80,
          width: 110,
          height: 35,
          description: 'Check authorization and log access',
          inputs: ['api-keys'],
          outputs: ['access-logs'],
          instruments: ['policy-rules']
        },
        {
          id: 'audit-compliance',
          name: 'Audit Compliance',
          type: 'process',
          x: 800,
          y: 380,
          width: 110,
          height: 35,
          description: 'Monitor regulatory compliance',
          inputs: ['access-logs'],
          outputs: [],
          instruments: ['policy-rules']
        }
      ],
      agents: [
        {
          id: 'db-systems',
          name: 'Database Systems',
          type: 'agent',
          x: 80,
          y: 60,
          width: 120,
          height: 45,
          description: 'Databases containing sensitive data'
        },
        {
          id: 'api-clients',
          name: 'API Clients',
          type: 'agent',
          x: 80,
          y: 380,
          width: 90,
          height: 45,
          description: 'Applications accessing via API'
        },
        {
          id: 'ai-models',
          name: 'AI/ML Models',
          type: 'agent',
          x: 950,
          y: 80,
          width: 120,
          height: 65,
          description: 'Machine learning systems'
        },
        {
          id: 'end-users',
          name: 'End Users',
          type: 'agent',
          x: 950,
          y: 380,
          width: 90,
          height: 45,
          description: 'Human users of applications'
        }
      ],
      connections: []
    },

    // L3 - Implementation Level (Lowest abstraction)
    {
      level: 3,
      name: "Implementation Level", 
      description: "Detailed implementation specifics and technical components",
      focus: "Technical implementation details and specific algorithms",
      objects: [
        {
          id: 'field-scanner',
          name: 'Field Scanner',
          type: 'object',
          x: 170,
          y: 165,
          width: 100,
          height: 40,
          description: 'Scans database fields for patterns',
          states: ['Scanning', 'Complete'],
          currentState: 'Scanning'
        },
        {
          id: 'regex-patterns',
          name: 'Regex Patterns',
          type: 'object',
          x: 170,
          y: 225,
          width: 100,
          height: 40,
          description: 'Pattern matching rules for data types',
          states: ['Loaded', 'Matching'],
          currentState: 'Loaded'
        },
        {
          id: 'ml-classifier',
          name: 'ML Classifier',
          type: 'object',
          x: 170,
          y: 285,
          width: 100,
          height: 40,
          description: 'Machine learning classification model',
          states: ['Trained', 'Classifying'],
          currentState: 'Trained'
        },
        {
          id: 'aes-256',
          name: 'AES-256 Engine',
          type: 'object',
          x: 360,
          y: 165,
          width: 110,
          height: 40,
          description: 'Advanced Encryption Standard implementation',
          states: ['Ready', 'Encrypting'],
          currentState: 'Ready'
        },
        {
          id: 'key-manager',
          name: 'Key Manager',
          type: 'object',
          x: 360,
          y: 225,
          width: 110,
          height: 40,
          description: 'Cryptographic key lifecycle management',
          states: ['Generating', 'Managing', 'Rotating'],
          currentState: 'Managing'
        },
        {
          id: 'hsm-module',
          name: 'HSM Module',
          type: 'object',
          x: 360,
          y: 285,
          width: 110,
          height: 40,
          description: 'Hardware Security Module for key storage',
          states: ['Secure', 'Processing'],
          currentState: 'Secure'
        },
        {
          id: 'token-generator',
          name: 'Token Generator',
          type: 'object',
          x: 560,
          y: 165,
          width: 110,
          height: 40,
          description: 'Algorithmic token generation system',
          states: ['Ready', 'Generating'],
          currentState: 'Ready'
        },
        {
          id: 'format-validator',
          name: 'Format Validator',
          type: 'object',
          x: 560,
          y: 225,
          width: 110,
          height: 40,
          description: 'Ensures tokens preserve original format',
          states: ['Validating', 'Confirmed'],
          currentState: 'Validating'
        },
        {
          id: 'database-store',
          name: 'Database Store',
          type: 'object',
          x: 560,
          y: 285,
          width: 110,
          height: 40,
          description: 'Persistent storage for tokens and mappings',
          states: ['Storing', 'Indexed'],
          currentState: 'Storing'
        },
        {
          id: 'jwt-tokens',
          name: 'JWT Tokens',
          type: 'object',
          x: 760,
          y: 165,
          width: 100,
          height: 40,
          description: 'JSON Web Tokens for API authentication',
          states: ['Valid', 'Expired'],
          currentState: 'Valid'
        },
        {
          id: 'oauth-handler',
          name: 'OAuth Handler',
          type: 'object',
          x: 760,
          y: 225,
          width: 100,
          height: 40,
          description: 'OAuth 2.0 authorization flow management',
          states: ['Authorized', 'Refreshing'],
          currentState: 'Authorized'
        },
        {
          id: 'rate-limiter',
          name: 'Rate Limiter',
          type: 'object',
          x: 760,
          y: 285,
          width: 100,
          height: 40,
          description: 'API request throttling mechanism',
          states: ['Monitoring', 'Limiting'],
          currentState: 'Monitoring'
        },
        {
          id: 'audit-db',
          name: 'Audit Database',
          type: 'object',
          x: 940,
          y: 225,
          width: 110,
          height: 40,
          description: 'Immutable audit log storage',
          states: ['Recording', 'Archived'],
          currentState: 'Recording'
        }
      ],
      processes: [
        {
          id: 'pattern-match',
          name: 'Pattern Match',
          type: 'process',
          x: 290,
          y: 80,
          width: 100,
          height: 35,
          description: 'Execute regex pattern matching',
          inputs: ['field-scanner', 'regex-patterns', 'ml-classifier'],
          outputs: [],
          agents: ['postgres-db', 'mongodb']
        },
        {
          id: 'ml-inference',
          name: 'ML Inference',
          type: 'process',
          x: 290,
          y: 375,
          width: 100,
          height: 35,
          description: 'Run machine learning classification',
          inputs: ['ml-classifier'],
          outputs: [],
          agents: ['redis-cache']
        },
        {
          id: 'key-derivation',
          name: 'Key Derivation',
          type: 'process',
          x: 490,
          y: 80,
          width: 100,
          height: 35,
          description: 'Generate encryption keys from master key',
          inputs: ['aes-256', 'key-manager', 'hsm-module'],
          outputs: []
        },
        {
          id: 'encrypt-transform',
          name: 'Encrypt Transform',
          type: 'process',
          x: 490,
          y: 375,
          width: 100,
          height: 35,
          description: 'Apply AES encryption transformation',
          inputs: [],
          outputs: []
        },
        {
          id: 'token-mapping',
          name: 'Token Mapping',
          type: 'process',
          x: 690,
          y: 80,
          width: 100,
          height: 35,
          description: 'Create bidirectional token mappings',
          inputs: ['token-generator', 'database-store', 'format-validator'],
          outputs: []
        },
        {
          id: 'format-preserve',
          name: 'Format Preserve',
          type: 'process',
          x: 690,
          y: 375,
          width: 100,
          height: 35,
          description: 'Ensure format preservation in tokens',
          inputs: ['database-store'],
          outputs: []
        },
        {
          id: 'auth-validate',
          name: 'Auth Validate',
          type: 'process',
          x: 880,
          y: 80,
          width: 100,
          height: 35,
          description: 'Validate JWT and OAuth credentials',
          inputs: ['jwt-tokens', 'oauth-handler', 'rate-limiter'],
          outputs: [],
          agents: ['rest-api', 'graphql-api']
        },
        {
          id: 'log-access',
          name: 'Log Access',
          type: 'process',
          x: 880,
          y: 375,
          width: 100,
          height: 35,
          description: 'Record all access attempts and results',
          inputs: ['audit-db'],
          outputs: [],
          agents: ['mobile-app']
        }
      ],
      agents: [
        {
          id: 'postgres-db',
          name: 'PostgreSQL',
          type: 'agent',
          x: 70,
          y: 80,
          width: 90,
          height: 35,
          description: 'PostgreSQL database instance'
        },
        {
          id: 'mongodb',
          name: 'MongoDB',
          type: 'agent',
          x: 70,
          y: 130,
          width: 90,
          height: 35,
          description: 'MongoDB document database'
        },
        {
          id: 'redis-cache',
          name: 'Redis Cache',
          type: 'agent',
          x: 70,
          y: 330,
          width: 90,
          height: 35,
          description: 'Redis in-memory cache'
        },
        {
          id: 'rest-api',
          name: 'REST API',
          type: 'agent',
          x: 1060,
          y: 80,
          width: 90,
          height: 35,
          description: 'RESTful API client'
        },
        {
          id: 'graphql-api',
          name: 'GraphQL API',
          type: 'agent',
          x: 1060,
          y: 130,
          width: 90,
          height: 35,
          description: 'GraphQL API client'
        },
        {
          id: 'mobile-app',
          name: 'Mobile App',
          type: 'agent',
          x: 1060,
          y: 330,
          width: 90,
          height: 35,
          description: 'Mobile application client'
        }
      ],
      connections: []
    }
  ];

  // Get current abstraction level data
  const currentLevel = abstractionLevels[selectedLevel];
  const currentObjects = currentLevel.objects;
  const currentProcesses = currentLevel.processes;
  const currentAgents = currentLevel.agents;

  // Dynamic animation sequences based on abstraction level
  const getAnimationSequence = (level: number) => {
    switch (level) {
      case 0: return [
        { step: 0, description: 'Stakeholders interact with Privacy Ecosystem', objects: ['data-stakeholders', 'privacy-ecosystem'] },
        { step: 1, description: 'System processes and protects sensitive data', objects: ['privacy-ecosystem', 'protect-data'] },
        { step: 2, description: 'Regulatory compliance enforced', objects: ['protect-data', 'compliance-framework'] }
      ];
      case 1: return [
        { step: 0, description: 'Data Sources provide Sensitive Data', objects: ['data-sources', 'sensitive-data'] },
        { step: 1, description: 'Data Ingested & Classified', objects: ['sensitive-data', 'data-ingestion'] },
        { step: 2, description: 'Data secured in Privacy Vault System', objects: ['data-ingestion', 'vault-system'] },
        { step: 3, description: 'Tokens generated and replaced', objects: ['vault-system', 'tokenization', 'privacy-tokens'] },
        { step: 4, description: 'Access governed by policies', objects: ['access-control', 'governance-policies', 'privacy-tokens'] }
      ];
      case 2: return [
        { step: 0, description: 'Multiple data types discovered', objects: ['pii-data', 'phi-data', 'pci-data'] },
        { step: 1, description: 'Data discovery and classification', objects: ['data-discovery', 'classification'] },
        { step: 2, description: 'Encryption engine processes data', objects: ['encrypt-data', 'encryption-engine'] },
        { step: 3, description: 'Tokens generated and validated', objects: ['generate-tokens', 'format-tokens', 'api-keys'] },
        { step: 4, description: 'Access validation and compliance audit', objects: ['validate-access', 'audit-compliance', 'access-logs'] }
      ];
      case 3: return [
        { step: 0, description: 'Field scanning with regex patterns', objects: ['field-scanner', 'regex-patterns', 'pattern-match'] },
        { step: 1, description: 'ML inference for classification', objects: ['ml-classifier', 'ml-inference'] },
        { step: 2, description: 'Key derivation and AES encryption', objects: ['key-derivation', 'aes-256', 'encrypt-transform'] },
        { step: 3, description: 'Token mapping and format preservation', objects: ['token-mapping', 'format-preserve'] },
        { step: 4, description: 'Authentication validation and audit logging', objects: ['auth-validate', 'log-access', 'audit-db'] }
      ];
      default: return [];
    }
  };

  const animationSequence = getAnimationSequence(selectedLevel);

  // Start animation
  const startAnimation = () => {
    setIsAnimating(true);
    setAnimationStep(0);
    
    const interval = setInterval(() => {
      setAnimationStep(prev => {
        if (prev >= animationSequence.length - 1) {
          clearInterval(interval);
          setIsAnimating(false);
          return 0;
        }
        return prev + 1;
      });
    }, 2000);
  };

  const toggleSection = (section: string) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  // Generate OPL (Object-Process Language) description
  const generateOPL = () => {
    const sentences = [
      `The ${companyData.company} Data Privacy Vault System is designed to handle sensitive data.`,
      "Sensitive Data, which includes PII, PHI, and PCI data, is prone to sprawl if not handled correctly.",
      "The Ingest & Classify process discovers this data and prepares it for isolation.",
      "The Isolate & Protect process moves the data into the Data Privacy Vault and applies polymorphic encryption.",
      "The Data Privacy Vault acts as a zero-trust, centralized, and protected single source of truth.",
      "The Tokenize & Replace process generates non-sensitive, format-preserving Privacy Tokens.",
      "These tokens are used in applications and AI models instead of the original raw data.",
      "The Governance Policy object defines fine-grained rules for access, residency, and compliance.",
      "The Govern & Mediate process uses this policy to handle all access requests, providing de-tokenized views only to authorized users, applications, or AI models.",
      "This system ensures full compliance with major regulations like GDPR, HIPAA, and PCI DSS."
    ];
    
    return sentences.join(' ');
  };

  // Dynamic connections based on abstraction level
  const allElements = [...currentObjects, ...currentProcesses, ...currentAgents];
  const elementMap = new Map(allElements.map(e => [e.id, e]));

  const getAttachmentPoint = (el: any, side: 'left' | 'right' | 'top' | 'bottom' | 'center') => {
    if (!el) return { x: 0, y: 0 };
    switch (side) {
      case 'left': return { x: el.x, y: el.y + el.height / 2 };
      case 'right': return { x: el.x + el.width, y: el.y + el.height / 2 };
      case 'top': return { x: el.x + el.width / 2, y: el.y };
      case 'bottom': return { x: el.x + el.width / 2, y: el.y + el.height };
      default: return { x: el.x + el.width / 2, y: el.y + el.height / 2 };
    }
  };

  const getConnectionsForLevel = (level: number) => {
    const connections: any[] = [];
    
    // Generate connections based on process inputs/outputs and instruments
    currentProcesses.forEach(process => {
      // Input connections (objects to processes) - choose best attachment points
      process.inputs?.forEach(inputId => {
        const inputElement = elementMap.get(inputId);
        if (inputElement) {
          // Determine best connection sides based on relative positions
          let startSide: 'left' | 'right' | 'top' | 'bottom' = 'right';
          let endSide: 'left' | 'right' | 'top' | 'bottom' = 'left';
          
          if (inputElement.x > process.x) {
            startSide = 'left';
            endSide = 'right';
          } else if (Math.abs(inputElement.x - process.x) < 50) {
            if (inputElement.y < process.y) {
              startSide = 'bottom';
              endSide = 'top';
            } else {
              startSide = 'top';
              endSide = 'bottom';
            }
          }
          
          connections.push({
            start: getAttachmentPoint(inputElement, startSide),
            end: getAttachmentPoint(process, endSide),
            markerEnd: 'url(#arrowhead)',
            type: 'input'
          });
        }
      });

      // Output connections (processes to objects)
      process.outputs?.forEach(outputId => {
        const outputElement = elementMap.get(outputId);
        if (outputElement) {
          let startSide: 'left' | 'right' | 'top' | 'bottom' = 'right';
          let endSide: 'left' | 'right' | 'top' | 'bottom' = 'left';
          
          if (outputElement.x < process.x) {
            startSide = 'left';
            endSide = 'right';
          } else if (Math.abs(outputElement.x - process.x) < 50) {
            if (outputElement.y > process.y) {
              startSide = 'bottom';
              endSide = 'top';
            } else {
              startSide = 'top';
              endSide = 'bottom';
            }
          }
          
          connections.push({
            start: getAttachmentPoint(process, startSide),
            end: getAttachmentPoint(outputElement, endSide),
            markerEnd: 'url(#arrowhead)',
            type: 'output'
          });
        }
      });

      // Instrument connections (from bottom of instrument to process)
      process.instruments?.forEach(instrumentId => {
        const instrumentElement = elementMap.get(instrumentId);
        if (instrumentElement) {
          let startSide: 'left' | 'right' | 'top' | 'bottom' = 'top';
          let endSide: 'left' | 'right' | 'top' | 'bottom' = 'bottom';
          
          if (process.y > instrumentElement.y) {
            startSide = 'bottom';
            endSide = 'top';
          }
          
          connections.push({
            start: getAttachmentPoint(instrumentElement, startSide),
            end: getAttachmentPoint(process, endSide),
            markerStart: 'url(#instrument-arrow)',
            markerEnd: 'url(#arrowhead)',
            type: 'instrument'
          });
        }
      });

      // Agent connections (to processes they trigger)
      process.agents?.forEach(agentId => {
        const agentElement = elementMap.get(agentId);
        if (agentElement) {
          let startSide: 'left' | 'right' | 'top' | 'bottom' = 'right';
          let endSide: 'left' | 'right' | 'top' | 'bottom' = 'left';
          
          if (agentElement.x > process.x) {
            startSide = 'left';
            endSide = 'right';
          }
          
          connections.push({
            start: getAttachmentPoint(agentElement, startSide),
            end: getAttachmentPoint(process, endSide),
            markerStart: 'url(#agent-arrow)',
            markerEnd: 'url(#arrowhead)',
            type: 'agent'
          });
        }
      });
    });

    return connections;
  };

  const connections = getConnectionsForLevel(selectedLevel);
  const [isProcessing, setIsProcessing] = useState(isCompleted ? false : true);
  const [showContent, setShowContent] = useState(isCompleted ?? false);
  const [mbseOpmDiagram, setMbseOpmDiagram] = useState<string>("");

  
  const openFullscreen = async () => {
    window.open(mbseOpmDiagram, "_blank");
  };
  
  const getMBSEOPMDiagram = async () => {
    setShowContent(true)
    setIsProcessing(true)
    
    const query = {
      "industry_name": companyData?.company || "Skyflow",
      "constraints": companyData?.challenges || "Data Privacy",
      "problem_context": companyData?.objective || "Blockchain Records",
      "company_context": companyData?.industry || "HIPAA"    
    };

    const response = await fetch(`${(import.meta as any).env.VITE_API_URL}/generate/opm`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json"
      },
      body: JSON.stringify(query)
    });
    if (response.ok) {
      const data = await response.json();
      setMbseOpmDiagram(data?.svg_url);
      console.log('Response data:', data);
    } else {
      console.error('Fetch error:', response.status, response.statusText);
    }
    setIsProcessing(false);
  };

  if (!showContent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 flex items-center justify-center">
        <div className="max-w-2xl mx-auto p-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <div className="p-6 rounded-2xl bg-gradient-to-r from-emerald-500/20 to-green-500/20 backdrop-blur-xl border border-slate-200/50 mb-6 inline-block">
              <Target className="w-16 h-16 text-emerald-600" />
            </div>
            <h1 className="sqsft-heading-1 mb-2">MBSE OPM Analysis</h1>
            <p className="sqsft-body" style={{ color: 'rgb(var(--color-text-secondary))' }}>
              Comprehensive diagram framing with constraints and success criteria.
            </p>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {
                getMBSEOPMDiagram();
              }}
              className="sqsft-btn-primary mt-8 text-lg px-8 py-4"
            >
              <Zap className="w-6 h-6" />
              <span>Start MBSE Analysis</span>
            </motion.button>
          </motion.div>
        </div>
      </div>
    );
  }

  // Processing View
  if (isProcessing) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'rgb(var(--color-bg-light))' }}>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center max-w-2xl mx-auto p-8"
        >
          <div className="relative mb-8">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="w-20 h-20 border-4 rounded-full mx-auto"
              style={{ borderColor: 'rgba(66, 102, 83, 0.2)', borderTopColor: 'rgb(var(--color-sage-green))' }}
            />
            <Sparkles className="w-8 h-8 absolute top-6 left-1/2 transform -translate-x-1/2" style={{ color: 'rgb(var(--color-deep-green))' }} />
          </div>
          
          <h2 className="text-3xl font-bold sqsft-gradient-text mb-4">Preparing MBSE OPM Analysis</h2>
          <p className="text-lg sqsft-body mb-8" style={{ color: 'rgb(var(--color-text-secondary))' }}>
            Setting up Model-Based Systems Engineering with Object-Process Methodology...
          </p>
          
          <div className="space-y-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1 }}
              className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl p-4 border border-slate-200/50 dark:border-slate-700/50"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm sqsft-heading-4">Reading Data</span>
                <CheckCircle2 className="w-4 h-4" style={{ color: 'rgb(var(--color-royal-gold))' }} />
              </div>
              <div className="w-full rounded-full h-2" style={{ backgroundColor: 'rgba(212, 175, 55, 0.2)' }}>
                <div className="h-2 rounded-full w-full" style={{ backgroundColor: 'rgb(var(--color-royal-gold))' }} />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 2 }}
              className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl p-4 border border-slate-200/50 dark:border-slate-700/50"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Collecting Context</span>
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'rgb(var(--color-sage-green))' }} />
              </div>
              <div className="w-full rounded-full h-2" style={{ backgroundColor: 'rgba(66, 102, 83, 0.2)' }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: '75%' }}
                  transition={{ duration: 2 }}
                  className="h-2 rounded-full"
                  style={{ backgroundColor: 'rgb(var(--color-sage-green))' }}
                />
              </div>
            </motion.div>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 3 }}
              className="bg-white/80 dark:bg-slate-800/80 backdrop-blur-xl rounded-xl p-4 border border-slate-200/50 dark:border-slate-700/50"
            >
              <div className="flex items-center justify-between mb-2">
                <span className="text-sm font-medium">Preparing MBSE OPM Model</span>
                <Loader2 className="w-4 h-4 animate-spin" style={{ color: 'rgb(var(--color-deep-green))' }} />
              </div>
              <div className="w-full rounded-full h-2" style={{ backgroundColor: 'rgba(13, 86, 48, 0.2)' }}>
                <motion.div
                  initial={{ width: 0 }}
                  animate={{ width: '45%' }}
                  transition={{ duration: 1.5 }}
                  className="h-2 rounded-full"
                  style={{ backgroundColor: 'rgb(var(--color-deep-green))' }}
                />
              </div>
            </motion.div>
          </div>
          
          <p className="text-sm text-slate-500 dark:text-slate-500 mt-6">
            Preparing your systems engineering analysis with OPM methodology...
          </p>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'rgb(var(--color-bg-light))' }}>
      {/* Header */}
      <div className="relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <motion.div
            animate={{ 
              x: [0, 100, 0],
              y: [0, -50, 0],
              rotate: [0, 180, 360]
            }}
            transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
            className="absolute top-20 left-20 w-32 h-32 rounded-full blur-xl" 
            style={{ background: 'linear-gradient(90deg, rgba(66, 102, 83, 0.2), rgba(13, 86, 48, 0.2))' }}
          />
        </div>

        <div className="max-w-7xl mx-auto px-8 pt-20">
          <div className="complete-definition-container" style={{
            position: "absolute",
            top: "16px",
            right: "10px"
          }}>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => {onComplete ? onComplete({data: true}):null}}
              className="sqsft-btn-primary"
            >
              <Award className="w-5 h-5" />
              <span>Complete Definition</span>
            </motion.button>
          </div>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
              className="text-6xl md:text-7xl sqsft-heading-1 mb-6 leading-tight"
            >
              MBSE OPM Flow
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="text-2xl md:text-3xl sqsft-body max-w-4xl mx-auto mb-8 font-light leading-relaxed" 
              style={{ color: 'rgb(var(--color-text-secondary))' }}
            >
              A Model-Based Systems Engineering view of the{' '}
              <span className="sqsft-gradient-text font-semibold">
                {companyData.company} Data Privacy Vault
              </span>{' '}
              using Object-Process Methodology.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.5 }}
              className="flex flex-wrap justify-center gap-6 mb-12"
            >
              {[
                { icon: Shield, text: "Zero-Trust Vault", color: "sqsft-gradient-primary" },
                { icon: Lock, text: "Polymorphic Encryption", color: "sqsft-gradient-primary" },
                { icon: Replace, text: "Data Tokenization", color: "sqsft-gradient-primary" },
                { icon: Award, text: "Global Compliance", color: "sqsft-gradient-primary" }
              ].map((item, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ delay: 0.6 + index * 0.1 }}
                  className="flex items-center sqsft-card backdrop-blur-xl px-6 py-3 rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105" 
                  style={{ backgroundColor: 'rgba(255, 255, 255, 0.7)' }}
                >
                  <div className={`w-8 h-8 rounded-lg ${item.color} flex items-center justify-center mr-3`}>
                    <item.icon className="w-4 h-4 text-white" />
                  </div>
                  <span className="text-sm sqsft-body font-medium">{item.text}</span>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </div>
      
      <div style={{display: "flex", justifyContent: "end" }} >
          <div  
            className="hidden md:flex items-center px-4 py-2 sqsft-btn-primary text-sm rounded-lg transition-all duration-200 shadow-sm hover:shadow-md"
            onClick={openFullscreen}
          >
            Open Image in new tab
          </div>
      </div>

      {/* Main Content */}
       <div className="max-w-7xl mx-auto px-8 pb-20" style={{
        overflow: "scroll"
       }}
       >
          <img
              src={mbseOpmDiagram}
              alt="Healthcare OPM Diagram"
              className={`${
                !mbseOpmDiagram ? 'opacity-0' : 'opacity-100'
              }`}
              // onLoad={handleImageLoad}
              // onError={handleImageError}
              style={{
                maxWidth: "max-content",
              }}
            />
       </div>
      
    </div>
  );
};

export default MBSEOPMFlow;